// i18next bootstrap.
//
// Mounted from `src/main.tsx` BEFORE React renders so the first `useTranslation`
// call has a populated dictionary. App.tsx wraps the tree in <Suspense> to
// cover the HTTP-backend load of the active locale's namespaces.
//
// Detection chain (pre-auth):
//   1. `?l=es` query param           — deeplink locale hint
//   2. localStorage `i18nextLng`     — set whenever the user switches
//   3. `navigator.language`          — browser default
//   4. fallbackLng (DEFAULT_LANGUAGE) — hard floor
//
// Authed users: `Profile.language` is canonical and overrides everything else.
// The language switcher / Profile bootstrap fires `i18n.changeLanguage` +
// `PATCH /profile {language: code}` to keep BE in sync (see Slice 5).
//
// supportedLngs + cleanCode + load:'languageOnly' filter every source to the
// SUPPORTED_LANGUAGES whitelist — i18next refuses to switch to a code outside
// the list, so we never bootstrap into an untranslated language.
//
// See `docs/i18n.md`.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './config';

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: SUPPORTED_LANGUAGES,
    fallbackLng: DEFAULT_LANGUAGE,
    // Reject "en-US" / "es-MX" region tails — we don't ship region-specific
    // bundles. cleanCode + load:'languageOnly' together normalize the
    // detected lang to its base subtag before any storage / lookup.
    load: 'languageOnly',
    cleanCode: true,
    nonExplicitSupportedLngs: false,

    // Namespaces co-located by feature. Bootstrap the always-needed ones
    // PLUS namespaces used by widely-reused components that would otherwise
    // mid-render-suspend the page-level Suspense boundary in App.tsx:
    //   - `home`    : HomePage (the root route — hit on every fresh session)
    //   - `listing` : DishInfoCard (rendered on Home, Profile, Listing
    //                 Details — its mid-render suspend was the cause of the
    //                 "page paints, then flashes spinner, then re-paints"
    //                 behavior on Profile refresh).
    // Truly page-specific namespaces (profile, orders, publish, auth, etc.)
    // stay lazy-loaded on first useTranslation call.
    ns: ['common', 'errors', 'home', 'listing'],
    defaultNS: 'common',
    fallbackNS: 'common',

    backend: {
      // Serve from /public/locales/<lng>/<ns>.json — Vite copies public/
      // to dist/ at build so the same URL works in dev and prod.
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    detection: {
      // Source order — the first hit that's in SUPPORTED_LANGUAGES wins;
      // anything else falls through. No 'cookie' / 'sessionStorage' / 'htmlTag'
      // sources — they add complexity without adding signal we don't already
      // get from localStorage + navigator.
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'l',
      lookupLocalStorage: 'i18nextLng',
      // Persist the resolved language so the next session boots into it.
      caches: ['localStorage'],
    },

    interpolation: {
      // React already escapes string interpolation in JSX, so don't
      // double-escape via i18next.
      escapeValue: false,
    },

    react: {
      // Use Suspense for the initial-bundle load (App.tsx wraps the tree).
      useSuspense: true,
    },
  });

export default i18n;
